import React from 'react';
import { motion } from 'framer-motion';
import Stars from './Stars';

const DayNightCycle = () => {
  // Función para crear puntos de una parábola normal (U)
  const createParabolicPath = (startX, endX, depth, points = 100) => {
    const path = [];
    const width = endX - startX;
    const a = 4 * depth / Math.pow(width, 2);  // Coeficiente para la parábola normal

    for (let i = 0; i <= points; i++) {
      const x = startX + width * (i / points);
      // y = a(x - h)² + k donde (h,k) es el vértice
      // Ahora la parábola abre hacia arriba
      const y = a * Math.pow(x - (startX + width / 2), 2);
      path.push([x, y]);
    }
    return path;
  };

  // Crear un único camino parabólico que ambos astros seguirán
  // Ajustamos la profundidad para controlar qué tan alto suben los astros
  const celestialPath = createParabolicPath(-400, 800, 200);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Reemplazamos el div con gradiente animado por uno transparente */}
      <div className="absolute inset-0 bg-transparent" />

      {/* Contenedor de la órbita */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-[800px] h-[400px]">
          {/* Sol */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 1, 1, 0, 0]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
              times: [0, 0.1, 0.35, 0.45, 1]
            }}
            className="absolute"
          >
            <motion.div
              initial={{ x: celestialPath[0][0], y: celestialPath[0][1] }}
              animate={{
                x: celestialPath.map(point => point[0]),
                y: celestialPath.map(point => point[1])
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
              className="relative w-32 h-32 flex items-center justify-center"
            >
              <motion.div
                animate={{
                  boxShadow: [
                    `0 0 30px rgba(255, 215, 0, 0.3), 0 0 60px rgba(255, 215, 0, 0.2), 0 0 90px rgba(255, 215, 0, 0.1)`,
                    `0 0 40px rgba(255, 215, 0, 0.4), 0 0 80px rgba(255, 215, 0, 0.3), 0 0 120px rgba(255, 215, 0, 0.2)`,
                    `0 0 30px rgba(255, 215, 0, 0.3), 0 0 60px rgba(255, 215, 0, 0.2), 0 0 90px rgba(255, 215, 0, 0.1)`
                  ]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute w-full h-full rounded-full bg-gradient-radial from-yellow-200 via-yellow-300 to-yellow-500"
                style={{
                  filter: 'blur(0.5px)',
                  transform: 'translate(-50%, -50%)',
                  left: '50%',
                  top: '50%'
                }}
              />
            </motion.div>
          </motion.div>

          {/* Luna */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0, 0, 1, 1, 0]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
              times: [0, 0.35, 0.45, 0.5, 0.85, 0.95]
            }}
            className="absolute"
          >
            <motion.div
              initial={{ x: celestialPath[0][0], y: celestialPath[0][1] }}
              animate={{
                x: celestialPath.map(point => point[0]),
                y: celestialPath.map(point => point[1])
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
              className="relative w-24 h-24 flex items-center justify-center"
            >
              <motion.div
                animate={{
                  boxShadow: [
                    `0 0 20px rgba(255, 255, 255, 0.3), 0 0 40px rgba(255, 255, 255, 0.2), 0 0 60px rgba(255, 255, 255, 0.1)`,
                    `0 0 30px rgba(255, 255, 255, 0.4), 0 0 60px rgba(255, 255, 255, 0.3), 0 0 90px rgba(255, 255, 255, 0.2)`,
                    `0 0 20px rgba(255, 255, 255, 0.3), 0 0 40px rgba(255, 255, 255, 0.2), 0 0 60px rgba(255, 255, 255, 0.1)`
                  ]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute w-full h-full rounded-full bg-gradient-radial from-gray-50 via-gray-100 to-blue-50"
                style={{
                  filter: 'blur(0.5px)',
                  transform: 'translate(-50%, -50%)',
                  left: '50%',
                  top: '50%'
                }}
              >
                {/* Cráteres de la luna con tonos más suaves */}
                <div className="w-4 h-4 rounded-full bg-gray-100 absolute top-4 left-4 opacity-40" />
                <div className="w-3 h-3 rounded-full bg-gray-100 absolute top-12 left-8 opacity-40" />
                <div className="w-5 h-5 rounded-full bg-gray-100 absolute top-8 right-6 opacity-40" />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Estrellas */}
      <motion.div
        animate={{
          opacity: [0, 0, 1, 1, 0]
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
          times: [0, 0.35, 0.5, 0.85, 1]
        }}
        className="absolute inset-0"
      >
        <Stars />
      </motion.div>
    </div>
  );
};

export default DayNightCycle; 